import { useAtom } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OftTypes } from 'structure/bridge/constants/bridgeTokens';
import {
  DECIMALS_SHOW,
  DECIMALS_SHOW_MOBILE,
} from 'structure/bridge/constants/constants';
import {
  DEFAULT_FEE_DECIMALS,
  DEFAULT_TOKEN_DECIMALS,
} from 'structure/bridge/constants/lazyerZeroChains';
import { bridgeAtom } from 'structure/bridge/store/bridgeAtom';
import { firstDecimals } from 'structure/bridge/utils/bridgeStateHelpers';
import { formatEther, formatUnits } from 'viem';
import { useConnection } from 'wagmi';
import { useIsMobile } from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';
import { defaultNetwork } from '@dapp/multi-lib/network';

interface BridgeUserBalanceAndFeeProps {
  tokenBalance: bigint;
  tokenFee: bigint;
  nativeFee: bigint;
  maxBridgeAmountProxy: bigint;
}

export const BridgeUserBalanceAndFee: React.FC<
  BridgeUserBalanceAndFeeProps
> = ({
  tokenFee,
  nativeFee,
  maxBridgeAmountProxy,
}: BridgeUserBalanceAndFeeProps) => {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  const { chain } = useConnection();
  const chainId = chain?.id ?? defaultNetwork.id;
  const [bridgeState, setBridgeState] = useAtom(bridgeAtom);
  const balanceValue = firstDecimals(
    parseFloat(
      formatUnits(
        bridgeState.userBalance,
        bridgeState.token?.decimals ?? DEFAULT_TOKEN_DECIMALS
      )
    ),
    isMobile ? DECIMALS_SHOW_MOBILE : DECIMALS_SHOW
  );
  const tokenBalanceFormatted = balanceValue?.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    setRendered(true);
  }, []);

  const onMaxClick = () => {
    const prevState = bridgeState;
    const amount =
      parseFloat(
        formatUnits(
          prevState.userBalance,
          bridgeState.token?.decimals ?? DEFAULT_TOKEN_DECIMALS
        )
      ) ?? 0;

    setBridgeState({
      ...prevState,
      amount: amount,
      nativeFee: BigInt(0),
      tokenFee: BigInt(0),
    });
  };

  const onMaxBridgeAmountClick = () => {
    const prevState = bridgeState;
    const amount = parseFloat(
      formatUnits(
        maxBridgeAmountProxy,
        bridgeState.token?.decimals ?? DEFAULT_TOKEN_DECIMALS
      )
    );
    setBridgeState({
      ...prevState,
      amount: amount,
      nativeFee: BigInt(0),
      tokenFee: BigInt(0),
    });
  };

  const tokenFeeString =
    tokenFee > BigInt(0) && bridgeState.amount > 0
      ? firstDecimals(
          parseFloat(
            formatUnits(
              tokenFee,
              bridgeState?.token?.decimals ?? DEFAULT_TOKEN_DECIMALS
            )
          ),
          DEFAULT_FEE_DECIMALS
        ) +
        ' ' +
        (bridgeState?.token?.symbol ?? '')
      : '0.00' + ' ' + (bridgeState?.token?.symbol ?? '');

  const nativeFeeString =
    nativeFee > BigInt(0) && bridgeState.amount > 0
      ? parseFloat(formatEther(nativeFee))
          .toFixed(DEFAULT_FEE_DECIMALS)
          .toString() + ((chain?.id && chain?.nativeCurrency?.symbol) ?? 'Gas')
      : '0.00' + ' ' + ((chain?.id && chain?.nativeCurrency?.symbol) ?? 'Gas');

  const destinationOftIsProxy =
    bridgeState.token?.oftType[bridgeState.dstChainId] === OftTypes.Proxy;

  const proxyEnabledAsDestination = bridgeState.token?.enableBridge[chainId];

  return (
    <div className='my-2 flex flex-col'>
      <div className={`flex ${isMobile && 'flex-col'} justify-between py-2`}>
        <div className='font-haben pr-2.5 text-sm font-normal text-white'>
          {t('BRIDGE_FEE')}:{' '}
          <span className='cursor-default text-white'>
            {bridgeState.tokenQuoteLoading
              ? 'Loading...'
              : tokenFeeString + ' + ' + nativeFeeString}
          </span>
        </div>
        <div className='font-haben pr-2.5 text-sm font-normal text-white'>
          {t('BALANCE')}:{' '}
          <button className='cursor-pointer text-white' onClick={onMaxClick}>
            {t('{{userBalance}}', tokenBalanceFormatted)}
          </button>
        </div>
      </div>

      {rendered && destinationOftIsProxy && proxyEnabledAsDestination && (
        <div className='font-haben pr-2.5 text-sm font-normal text-white'>
          {t('Available in bridge')}:{' '}
          <button
            className='cursor-pointer text-white'
            onClick={onMaxBridgeAmountClick}
          >
            {firstDecimals(
              parseInt(
                formatUnits(
                  maxBridgeAmountProxy,
                  bridgeState.token?.decimals ?? DEFAULT_TOKEN_DECIMALS
                )
              ),
              DECIMALS_SHOW
            ).toString()}{' '}
            {bridgeState.token?.symbol}
          </button>
        </div>
      )}
    </div>
  );
};
